import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCw, Users, Calendar, Scale, FileText, Plus, Trash2 } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid, gradient }) {
  return (
    <div className="card-elevated p-6" data-testid={testid}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-md`}>
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="font-serif text-3xl text-[#0A2540] font-semibold">{value ?? "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [cases, setCases] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", content: "", keywords: "", cover_image: "" });

  const refresh = useCallback(async () => {
    try {
      const [s, l, b, bl, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/leads"),
        api.get("/bookings"),
        api.get("/admin/blogs"),
        api.get("/cases"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setBookings(b.data);
      setBlogs(bl.data);
      setCases(c.data);
    } catch {
      localStorage.removeItem("admin_token");
      nav("/admin/login");
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      nav("/admin/login");
      return;
    }
    refresh();
  }, [nav, refresh]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const syncCases = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/cases/sync");
      toast.success(`Synced ${data.synced} cases`);
      await refresh();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const createBlog = async (e) => {
    e.preventDefault();
    try {
      await api.post("/blogs", {
        title: blogForm.title,
        excerpt: blogForm.excerpt,
        content: blogForm.content,
        cover_image: blogForm.cover_image || null,
        keywords: blogForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Blog published");
      setBlogForm({ title: "", excerpt: "", content: "", keywords: "", cover_image: "" });
      refresh();
    } catch {
      toast.error("Failed to publish");
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/blogs/${id}`);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: "Leads" },
    { id: "bookings", label: "Bookings" },
    { id: "cases", label: "Cases" },
    { id: "blogs", label: "Blogs" },
  ];

  const inputCls = "w-full bg-white border border-slate-200 px-3.5 py-3 text-slate-800 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/15 rounded-lg text-sm transition-all";

  return (
    <div className="pt-[72px] min-h-screen bg-slate-50/50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1D4ED8] mb-2">
              Chambers Console
            </div>
            <h1 className="font-serif text-4xl text-[#0A2540] font-semibold">Administration</h1>
          </div>
          <button onClick={logout} className="ghost-btn text-sm" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex gap-1 border-b border-slate-200 mb-8 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm tracking-wide border-b-2 transition-all whitespace-nowrap font-medium ${tab === t.id
                  ? "border-[#1D4ED8] text-[#1D4ED8]"
                  : "border-transparent text-slate-500 hover:text-[#0A2540]"
                }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            <StatCard icon={Users} label="Total Leads" value={stats?.leads} testid="stat-leads" gradient="from-[#1D4ED8] to-[#0A2540]" />
            <StatCard icon={Calendar} label="Bookings" value={stats?.bookings} testid="stat-bookings" gradient="from-emerald-500 to-emerald-700" />
            <StatCard icon={Scale} label="Cases" value={stats?.cases} testid="stat-cases" gradient="from-violet-500 to-violet-700" />
            <StatCard icon={FileText} label="Blogs" value={stats?.blogs} testid="stat-blogs" gradient="from-amber-500 to-amber-700" />
            <StatCard icon={Users} label="New Leads" value={stats?.new_leads} testid="stat-new-leads" gradient="from-rose-500 to-rose-700" />
            <StatCard icon={Calendar} label="Upcoming" value={stats?.upcoming_bookings} testid="stat-upcoming" gradient="from-sky-500 to-sky-700" />
          </div>
        )}

        {tab === "leads" && (
          <div className="card-elevated overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-slate-200 text-left bg-slate-50">
                  {["Name", "Phone", "Email", "Source", "Status", "Message"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 text-slate-700 hover:bg-slate-50">
                    <td className="px-5 py-4 font-medium text-[#0A2540]">{l.name}</td>
                    <td className="px-5 py-4">{l.phone}</td>
                    <td className="px-5 py-4">{l.email || "—"}</td>
                    <td className="px-5 py-4 text-slate-500">{l.source}</td>
                    <td className="px-5 py-4"><span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] font-semibold bg-blue-50 px-2 py-1 rounded-full">{l.status}</span></td>
                    <td className="px-5 py-4 text-slate-500 max-w-xs truncate">{l.message || "—"}</td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "bookings" && (
          <div className="card-elevated overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead>
                <tr className="border-b border-slate-200 text-left bg-slate-50">
                  {["Date", "Time", "Name", "Phone", "Payment", "Status"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 text-slate-700 hover:bg-slate-50">
                    <td className="px-5 py-4 font-medium text-[#0A2540]">{b.date}</td>
                    <td className="px-5 py-4">{b.time}</td>
                    <td className="px-5 py-4">{b.name}</td>
                    <td className="px-5 py-4">{b.phone}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-500">{b.payment_id}</td>
                    <td className="px-5 py-4"><span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold bg-emerald-50 px-2 py-1 rounded-full">{b.status}</span></td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No bookings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "cases" && (
          <div>
            <div className="flex justify-end mb-5">
              <button onClick={syncCases} disabled={syncing} className="ghost-btn text-sm" data-testid="sync-cases-btn">
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync eCourts"}
              </button>
            </div>
            <div className="card-elevated overflow-x-auto">
              <table className="w-full text-sm" data-testid="cases-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left bg-slate-50">
                    {["Title", "Court", "Type", "Status"].map((h) => (
                      <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 text-slate-700 hover:bg-slate-50">
                      <td className="px-5 py-4 font-serif font-semibold text-[#0A2540]">{c.title}</td>
                      <td className="px-5 py-4 text-slate-500">{c.court}</td>
                      <td className="px-5 py-4">{c.case_type}</td>
                      <td className="px-5 py-4"><span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] font-semibold bg-blue-50 px-2 py-1 rounded-full">{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "blogs" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={createBlog} className="card-elevated p-6 space-y-4" data-testid="blog-create-form">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1D4ED8]">New Article</div>
              <input required placeholder="Title" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} className={inputCls} data-testid="blog-title-input" />
              <input required placeholder="Excerpt (1-2 sentences)" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} className={inputCls} />
              <textarea required rows={10} placeholder="Content (paragraphs separated by blank line)" value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} className={inputCls} />
              <input placeholder="Keywords (comma-separated)" value={blogForm.keywords} onChange={(e) => setBlogForm({ ...blogForm, keywords: e.target.value })} className={inputCls} />
              <input placeholder="Cover image URL (optional)" value={blogForm.cover_image} onChange={(e) => setBlogForm({ ...blogForm, cover_image: e.target.value })} className={inputCls} />
              <button type="submit" className="primary-btn w-full justify-center text-sm" data-testid="publish-blog-btn">
                <Plus size={14} /> Publish Article
              </button>
            </form>

            <div className="space-y-3" data-testid="blogs-list">
              {blogs.map((b) => (
                <div key={b.id} className="card-elevated p-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-serif text-lg text-[#0A2540] font-semibold">{b.title}</div>
                    <div className="text-xs text-slate-400 mt-1">/{b.slug}</div>
                    <div className="text-xs text-slate-500 mt-2 line-clamp-2">{b.excerpt}</div>
                  </div>
                  <button
                    onClick={() => deleteBlog(b.id)}
                    className="text-slate-400 hover:text-rose-500 p-2 transition-colors"
                    data-testid={`delete-blog-${b.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {blogs.length === 0 && <div className="text-slate-500 text-center py-10">No articles yet.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
